import { MenuIcon, MenuItem, MenuText, Tooltip, WidgetListItem } from "pixel";
import { Link, useLocation } from "react-router-dom";
import { matchCurrentPath } from "../../Layout/utils";
import { getAccessRoles } from "../../ProtectedRoutes";
import { RenderByUserRole } from "../accessControl/ComponentRBAC";
import { getMenuRoles } from "../accessControl/routeAccess";
import { useUserInfo } from "../context/userinfo/useUserInfo";
import useUIState from "../hooks/useUIState";
import { isAuthorisedRole } from "./utils";

export const ProtectedMenu = ({
  matchRoute,
  matchExact,
  children,
}: {
  matchRoute: string | string[];
  children: React.ReactNode;
  matchExact?: boolean;
}) => {
  const userInfo = useUserInfo();
  const currentRoles = userInfo?.data.userInfo?.roles;
  const activeTenant = userInfo?.data?.activeTenant;
  return isAuthorisedRole(
    getMenuRoles(matchRoute, activeTenant, matchExact) ?? [],
    currentRoles
  ) ? (
    children
  ) : (
    <></>
  );
};

export const ProtectedMenuItem = ({
  redirectTo,
  label,
  route,
}: {
  redirectTo: string;
  label: string;
  route?: string;
}) => {
  const location = useLocation();
  const userInfo = useUserInfo();
  const { handleToggleMobileMenu } = useUIState();
  const currentRoles = userInfo?.data.userInfo?.roles;

  const requiredRoute = redirectTo.split("?")[0];
  const activeTenant = userInfo?.data?.activeTenant;
  const accessRoles = route
    ? getAccessRoles(route, activeTenant)
    : getAccessRoles(requiredRoute, activeTenant);

  return (
    isAuthorisedRole(accessRoles ?? [], currentRoles) && (
      <WidgetListItem active={matchCurrentPath(location, requiredRoute)}>
        <Link to={redirectTo} onClick={() => handleToggleMobileMenu(true)}>
          {label}
        </Link>
      </WidgetListItem>
    )
  );
};

export const ProtectedExternalLink = ({
  link,
  label,
  shouldOpenLink = true,
  tooltipText,
  resource = "routeHelpers.ProtectedExternalLink",
}: {
  readonly link: string;
  readonly label: string;
  readonly shouldOpenLink?: boolean;
  readonly tooltipText?: string;
  readonly resource?: string;
}) => {
  const { isSideBarCollapsed } = useUIState();

  return (
    <RenderByUserRole resource={resource}>
      <MenuItem>
        <Link
          to={link}
          onClick={
            shouldOpenLink
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {tooltipText ? (
            <Tooltip
              placement="right"
              content={tooltipText}
              hidden={!isSideBarCollapsed}
              customClass="block"
            >
              <MenuIcon>ExternalLink</MenuIcon>
            </Tooltip>
          ) : (
            <MenuIcon>ExternalLink</MenuIcon>
          )}
          <MenuText>{label}</MenuText>
        </Link>
      </MenuItem>
    </RenderByUserRole>
  );
};

export const ProtectedExternalLinkButton = ({
  link,
  label,
  shouldOpenLinkHandler,
  tooltipText,
}: {
  readonly link: string;
  readonly label: string;
  readonly tooltipText?: string;
  readonly shouldOpenLinkHandler?: () => Promise<boolean>;
}) => {
  const { isSideBarCollapsed } = useUIState();
  const handleClick = async () => {
    if (shouldOpenLinkHandler && (await shouldOpenLinkHandler())) {
      window.open(link, "_blank", "noopener noreferrer");
    }
  };

  return (
    <RenderByUserRole resource="routeHelpers.ProtectedExternalLinkButton">
      <MenuItem>
        <button onClick={handleClick} className="flex flex-1">
          {tooltipText ? (
            <Tooltip
              placement="right"
              content={tooltipText}
              hidden={!isSideBarCollapsed}
              customClass="block"
            >
              <MenuIcon>ExternalLink</MenuIcon>
            </Tooltip>
          ) : (
            <MenuIcon>ExternalLink</MenuIcon>
          )}
          <MenuText>{label}</MenuText>
        </button>
      </MenuItem>
    </RenderByUserRole>
  );
};
